import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Checkbox,
  Text,
  UnstyledButton,
  Popover,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconChevronDown, IconSearch, IconX } from "@tabler/icons-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  maxHeight?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchablethroughapi?: boolean;
  onSearch?: (searchTerm: string) => void;
  searchLoading?: boolean;
  error?: boolean;
  errorMessage?: string;
  // ENHANCED RESPONSIVE PROPS
  compact?: boolean; // Forces compact mode regardless of screen size
  size?: "xs" | "sm" | "md" | "lg"; // Size prop to control overall sizing
  allowShrink?: boolean; // NEW: Allows component to shrink below normal minimums
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  maxHeight = 200,
  searchable = false,
  searchPlaceholder = "Search options...",
  searchablethroughapi = false,
  onSearch,
  searchLoading = false,
  error = false,
  errorMessage = "Failed to fetch data",
  // ENHANCED PROPS WITH DEFAULTS
  compact = false,
  size = "md",
  allowShrink = false, // NEW PROP
}) => {
  const [opened, setOpened] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownHeight, setDropdownHeight] = useState<number | null>(null);
  const [availableScreenSpace, setAvailableScreenSpace] = useState<number>(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  // ENHANCED SIZE CONFIGURATIONS - FIXED DROPDOWN WIDTHS
  const sizeConfig = {
    xs: {
      itemHeight: 28,
      searchHeight: 36,
      padding: 6,
      buttonPadding: "4px 6px",
      textSize: "xs",
      iconSize: 10,
      labelSize: "xs",
      labelMargin: 2,
      maxLabelLength: allowShrink ? 15 : 15,
      // minWidth: allowShrink ? "60px" : "80px",
      // maxWidth: allowShrink ? "100px" : "120px",
      // FIXED: Same dropdown width regardless of allowShrink
      dropdownMinWidth: "200px",
      dropdownMaxWidth: "250px",
    },
    sm: {
      itemHeight: 32,
      searchHeight: 40,
      padding: 8,
      buttonPadding: "5px 8px",
      textSize: "xs",
      iconSize: 11,
      labelSize: "xs",
      labelMargin: 3,
      maxLabelLength: allowShrink ? 30 : 30,
      // minWidth: allowShrink ? "80px" : "100px",
      // maxWidth: allowShrink ? "120px" : "140px",
      // FIXED: Same dropdown width regardless of allowShrink
      dropdownMinWidth: "240px",
      dropdownMaxWidth: "290px",
    },
    md: {
      itemHeight: 38,
      searchHeight: 44,
      padding: 10,
      buttonPadding: "8px 10px",
      textSize: "sm",
      iconSize: 13,
      labelSize: "sm",
      labelMargin: 4,
      maxLabelLength: allowShrink ? 30 : 30,

      dropdownMinWidth: "300px",
      dropdownMaxWidth: "400px",
    },
    lg: {
      itemHeight: 44,
      searchHeight: 48,
      padding: 12,
      buttonPadding: "10px 14px",
      textSize: "md",
      iconSize: 15,
      labelSize: "md",
      labelMargin: 5,
      maxLabelLength: allowShrink ? 30 : 30,

      dropdownMinWidth: "350px",
      dropdownMaxWidth: "450px",
    },
  };

  // Get current size configuration
  const currentSize = sizeConfig[size];
  const ITEM_HEIGHT = currentSize.itemHeight;
  const SEARCH_HEIGHT = currentSize.searchHeight;

  // Check if this is a single-selection mode
  const isSingleSelectionMode = () => {
    return (
      label &&
      (label.toLowerCase().includes("metric") ||
        label.toLowerCase().includes("month") ||
        label.toLowerCase().includes("week") ||
        label.toLowerCase().includes("period") ||
        label.toLowerCase().includes("facilities") ||
        label.toLowerCase().includes("specialties"))
    );
  };

  // Check if cross icon should be hidden
  const shouldHideCrossIcon = () => {
    return (
      label &&
      (label.toLowerCase().includes("metric") ||
        label.toLowerCase().includes("month") ||
        label.toLowerCase().includes("period") ||
        label.toLowerCase().includes("week"))
    );
  };

  // Calculate available screen space for dropdown
  const calculateAvailableSpace = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const maxSpace = Math.max(spaceBelow, spaceAbove);
      setAvailableScreenSpace(maxSpace);
    }
  };

  // Check screen size and calculate available space
  useEffect(() => {
    const checkScreenSize = () => {
      calculateAvailableSpace();
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    window.addEventListener("scroll", calculateAvailableSpace);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
      window.removeEventListener("scroll", calculateAvailableSpace);
    };
  }, []);

  // Recalculate space when dropdown opens
  useEffect(() => {
    if (opened) {
      calculateAvailableSpace();
    }
  }, [opened]);

  // Handle search input change
  const handleSearchChange = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
    if (searchablethroughapi && onSearch) {
      onSearch(newSearchValue);
    }
  };

  // Filter options based on search value
  const filteredOptions =
    searchable && !searchablethroughapi
      ? options?.filter((option) =>
          option?.label?.toLowerCase()?.includes(searchValue.toLowerCase())
        )
      : options;

  const singleSelectionMode = isSingleSelectionMode();

  // SIMPLIFIED: Use all available space minus 30px from bottom
  // FIXED: Improved height calculation for multi-select
  useEffect(() => {
    if (opened && !error && !searchLoading && availableScreenSpace > 0) {
      const searchHeight = searchable ? SEARCH_HEIGHT : 0;

      // Calculate items more precisely
      const hasSelectAll =
        !singleSelectionMode && filteredOptions?.length > 0;
      const selectAllItems = hasSelectAll ? 1 : 0;
      const totalItems = filteredOptions?.length + selectAllItems;

      // More precise height calculation
      const itemsHeight = totalItems * ITEM_HEIGHT;
      const containerPadding = (currentSize.padding / 3) * 2; // py={currentSize.padding / 3} = top + bottom padding
      const itemMargins = totalItems * 4; // Each item has 2px top + 2px bottom margin (4px total)

      // Calculate exact content height
      const exactContentHeight =
        searchHeight + itemsHeight + containerPadding + itemMargins;

      // Use available space minus margin
      const maxAllowedHeight = availableScreenSpace - 30;

      let finalHeight;
      if (totalItems === 0) {
        // No content - small fixed height
        finalHeight = 80;
      } else if (exactContentHeight <= maxAllowedHeight) {
        // Content fits perfectly - use exact height
        finalHeight = exactContentHeight;
      } else {
        // Content is larger - use all available space with scrolling
        finalHeight = maxAllowedHeight;
      }

      // Ensure minimum usable height
      const minHeight = Math.max(
        80,
        ITEM_HEIGHT + searchHeight + containerPadding + 8
      );
      finalHeight = Math.max(minHeight, finalHeight);

      setDropdownHeight(finalHeight);
    } else if (opened && (error || searchLoading)) {
      // Error/loading states - smaller fixed height
      setDropdownHeight(Math.min(100, availableScreenSpace - 30));
    }
  }, [
    opened,
    filteredOptions?.length,
    searchable,
    singleSelectionMode,
    error,
    searchLoading,
    availableScreenSpace,
    ITEM_HEIGHT,
    SEARCH_HEIGHT,
    currentSize.padding,
  ]);

  // Updated getMaxVisibleItems for reference
  const getMaxVisibleItems = () => {
    const searchHeight = searchable ? SEARCH_HEIGHT : 0;
    const containerPadding = (currentSize.padding / 3) * 2;
    const availableHeightForItems =
      availableScreenSpace - 30 - searchHeight - containerPadding;
    return Math.floor(availableHeightForItems / (ITEM_HEIGHT + 4)); // +4 for item margins
  };

  // Check if this is the "All Time" option that should be disabled
  const isAllTimeDisabled = () => {
    const buttonLabel = getButtonLabel();
    return buttonLabel === "All Time";
  };

  // Check if all individual options are selected
  const areAllIndividualOptionsSelected = () => {
    return value?.length === options?.length;
  };

  const toggleOption = (optionValue: string) => {
    if (isSingleSelectionMode()) {
      onChange([optionValue]);
    } else {
      if (value.includes(optionValue)) {
        onChange(value?.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    }
  };

  const handleSelectAll = () => {
    if (isSingleSelectionMode()) {
      return;
    }

    if (areAllIndividualOptionsSelected()) {
      onChange([]);
    } else {
      onChange([]); //STATUS CHANGES
    }
  };

  const getButtonLabel = () => {
    if (error) {
      return errorMessage;
    }

    if (value?.length === 0) {
      // Use shorter placeholders for small screens and allowShrink mode
      if (allowShrink || size === "xs") {
        if (placeholder === "All Facilities") return "All Facilities";
        if (placeholder === "All Specialties") return "All Specialties";
        if (placeholder === "Select Period") return "Select Period";
        if (placeholder?.includes("Start Week")) return "Start Week";
        if (placeholder?.includes("End Week")) return "End Week";
        if (placeholder?.includes("Start Month")) return "Start Month";
        if (placeholder?.includes("End Month")) return "End Month";
        if (placeholder === "Select Message Status") return "Status";
      }
      return placeholder;
    }

    if (isSingleSelectionMode()) {
      if (value?.length === 1) {
        const selectedOption = options?.find(
          (option) => option.value === value[0]
        );
        const label = selectedOption?.label || "";
        // Use size-based truncation
        if (label.length > currentSize.maxLabelLength) {
          return label.substring(0, currentSize.maxLabelLength) + "...";
        }
        return label;
      }
      return placeholder;
    }

    // Multi selection mode with shorter labels for small sizes
    if (areAllIndividualOptionsSelected()) {
      if (allowShrink || size === "xs") {
        if (placeholder === "Select Status") return "All Status";
        if (placeholder === "Select Topic") return "All Topic";
        if (placeholder === "Select Time") return "All Time";
        if (placeholder === "Select Facility" || placeholder === "All Facility")
          return "All Facility";
        if (
          placeholder === "Select Speciality" ||
          placeholder === "All Speciality"
        )
          return "All Speciality";
        return "All Selected";
      } else {
        if (placeholder === "Select Status") return "All Queries";
        if (placeholder === "Select Topic") return "All Topics";
        if (placeholder === "Select Time") return "All Time";
        if (placeholder === "Select Facility" || placeholder === "All Facility")
          return "All Facilities";
        if (
          placeholder === "Select Speciality" ||
          placeholder === "All Speciality"
        )
          return "All Specialities";
        return "All Selected";
      }
    }

    if (value?.length === 1) {
      const label =
        options?.find((option) => option?.value === value[0])?.label || "";
      if (label?.length > currentSize.maxLabelLength) {
        return label?.substring(0, currentSize.maxLabelLength) + "...";
      }
      return label;
    }

    return `${value?.length} selected`;
  };

  // Helper function to get "Select All" text based on placeholder
  const getSelectAllText = () => {
    if (searchable && searchValue) {
      return "Select All Filtered";
    }

    if (placeholder === "Select Status") {
      return "All Queries";
    } else if (placeholder === "Select Topic") {
      return "All Topics";
    } else if (placeholder === "Select Message Status") {
      return "All Status";
    } else if (placeholder === "Select Time") {
      return "All Time";
    } else if (
      placeholder === "Select Facility" ||
      placeholder === "All Facility"
    ) {
      return "All Facilities";
    } else if (
      placeholder === "Select Speciality" ||
      placeholder === "All Speciality"
    ) {
      return "All Specialities";
    } else {
      return "All Selected";
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    if (searchablethroughapi && onSearch) {
      onSearch("");
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        opened
      ) {
        setOpened(false);
        setSearchValue("");
        if (searchablethroughapi && onSearch) {
          onSearch("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [opened, searchablethroughapi, onSearch]);

  // Focus search input when popover opens
  useEffect(() => {
    if (opened && searchable && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [opened, searchable]);

  // Handle popover open/close
  const handleTogglePopover = () => {
    if (isAllTimeDisabled()) {
      return;
    }
    const newOpened = !opened;
    setOpened(newOpened);
    if (!newOpened) {
      setSearchValue("");
      if (searchablethroughapi && onSearch) {
        onSearch("");
      }
    }
  };

  // Modified option selection logic
  const handleOptionToggle = (
    optionValue: string,
    event?: React.MouseEvent | React.ChangeEvent
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (areAllIndividualOptionsSelected()) {
      onChange([optionValue]);
    } else {
      toggleOption(optionValue);

      if (isSingleSelectionMode()) {
        setOpened(false);
        setSearchValue("");
        if (searchablethroughapi && onSearch) {
          onSearch("");
        }
      }
    }
  };

  // Handle select all
  const handleSelectAllClick = (
    event?: React.MouseEvent | React.ChangeEvent
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    handleSelectAll();
  };

  // Get responsive styles using size configuration
  const getButtonStyles = () => {
    const isDisabled = disabled || isAllTimeDisabled();
    return {
      border: error ? "1px solid #fa5252" : "1px solid #dee2e6",
      borderRadius: "4px",
      padding: currentSize.buttonPadding,
      width: "100%",
      // minWidth: currentSize.minWidth,
      // maxWidth: currentSize.maxWidth,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: isDisabled ? "#f1f3f5" : error ? "#fff5f5" : "white",
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.6 : 1,
    };
  };

  const getDropdownStyles = () => ({
    minWidth: currentSize.dropdownMinWidth,
    maxWidth: currentSize.dropdownMaxWidth,
    boxShadow: "0 8px 16px gray, 0 4px 16px gray",
    borderRadius: "4px",
    overflow: "hidden",
    maxHeight: dropdownHeight ? `${dropdownHeight}px` : "auto",
  });

  // Enhanced options container styles
  const getOptionsContainerStyles = (): React.CSSProperties => {
    const searchHeight = searchable ? SEARCH_HEIGHT : 0;
    const containerHeight = dropdownHeight
      ? dropdownHeight - searchHeight
      : "auto";
    const maxVisibleItems = getMaxVisibleItems();

    return {
      height: containerHeight,
      maxHeight: containerHeight,
      overflowY: filteredOptions?.length > maxVisibleItems ? "auto" : "hidden",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: "#638def transparent",
    };
  };

  // Helper function to check if text needs tooltip
  const needsTooltip = (text: string, maxLength: number) => {
    return text.length > maxLength;
  };

  // Check if component should be functionally disabled
  const isComponentDisabled = disabled || isAllTimeDisabled() || error;

  return (
    <Box>
      {label && (
        <Text
          style={{
            whiteSpace: "nowrap",
            flex: 1,
          }}
          size={currentSize.labelSize}
          fw={500}
          mb={currentSize.labelMargin}
          color={error ? "#fa5252" : undefined}
        >
          {label}
        </Text>
      )}

      <Popover
        opened={opened && !isComponentDisabled}
        position='bottom-start'
        width='target'
        withinPortal
        transitionProps={{ transition: "fade", duration: 200 }}
        styles={{
          dropdown: getDropdownStyles(),
        }}
      >
        <Popover.Target>
          <UnstyledButton
            size={size}
            ref={buttonRef}
            onClick={handleTogglePopover}
            disabled={isComponentDisabled}
            style={getButtonStyles()}
          >
            <Text
              size={currentSize.textSize}
              color={
                error
                  ? "#fa5252"
                  : value?.length === 0
                  ? "dimmed"
                  : isAllTimeDisabled()
                  ? "dimmed"
                  : undefined
              }
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                textAlign: "left",
              }}
            >
              {getButtonLabel()}
            </Text>
            {isSingleSelectionMode() &&
            value?.length > 0 &&
            !shouldHideCrossIcon() ? (
              <IconX
                size={currentSize.iconSize}
                style={{
                  marginLeft: "8px",
                  flexShrink: 0,
                  opacity: isComponentDisabled ? 0.5 : 1,
                  cursor: isComponentDisabled ? "not-allowed" : "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isComponentDisabled) {
                    onChange([]);
                  }
                }}
              />
            ) : (
              <IconChevronDown
                size={currentSize.iconSize}
                style={{
                  transform:
                    opened && !isComponentDisabled ? "rotate(180deg)" : "none",
                  transition: "transform 200ms ease",
                  marginLeft: "8px",
                  flexShrink: 0,
                  opacity: isComponentDisabled ? 0.5 : 1,
                }}
              />
            )}
          </UnstyledButton>
        </Popover.Target>

        <Popover.Dropdown p={0} ref={dropdownRef}>
          <Box>
            {searchable && (
              <Box
                p={currentSize.padding / 2}
                style={{
                  borderBottom: "1px solid #dee2e6",
                  boxShadow:
                    "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)",
                }}
              >
                <TextInput
                  ref={searchInputRef}
                  size={size}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.stopPropagation();
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  leftSection={<IconSearch size={currentSize.iconSize} />}
                  rightSection={
                    searchLoading ? (
                      <div
                        style={{
                          width: currentSize.iconSize,
                          height: currentSize.iconSize,
                          border: "2px solid #f3f3f3",
                          borderTop: "2px solid #638def",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                    ) : searchValue ? (
                      <IconX
                        size={currentSize.iconSize}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSearch();
                        }}
                      />
                    ) : null
                  }
                  rightSectionPointerEvents='auto'
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{ width: "100%" }}
                  autoFocus
                />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </Box>
            )}

            <Box ref={optionsContainerRef} style={getOptionsContainerStyles()}>
              <Box
                py={currentSize.padding / 3}
                px={2}
                style={{ overflow: "hidden" }}
              >
                {error ? (
                  <Box p={currentSize.padding} mx={4}>
                    <Text
                      size={currentSize.textSize}
                      color='#fa5252'
                      ta='center'
                    >
                      {errorMessage}
                    </Text>
                  </Box>
                ) : (
                  <>
                    {searchLoading ? (
                      <Box p={currentSize.padding} mx={4}>
                        <Text
                          size={currentSize.textSize}
                          color='dimmed'
                          ta='center'
                        >
                          Searching...
                        </Text>
                      </Box>
                    ) : (
                      <>
                        {options?.length === 0 ? (
                          <Box p={currentSize.padding} mx={4}>
                            <Text
                              size={currentSize.textSize}
                              color='dimmed'
                              ta='center'
                            >
                              {searchablethroughapi && searchValue
                                ? "No results found"
                                : "No data available"}
                            </Text>
                          </Box>
                        ) : (
                          <>
                            {!isSingleSelectionMode() &&
                              filteredOptions?.length > 0 && (
                                <UnstyledButton
                                  onClick={handleSelectAllClick}
                                  style={{
                                    width: "100%",
                                    height: `${ITEM_HEIGHT}px`,
                                    padding: `${currentSize.padding / 2}px ${
                                      currentSize.padding
                                    }px`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: `${currentSize.padding}px`,
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    borderRadius: "4px",
                                    margin: "2px 4px",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#e7f5ff";
                                    e.currentTarget.style.borderColor =
                                      "#339af0";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                    e.currentTarget.style.borderColor =
                                      "transparent";
                                  }}
                                >
                                  <Checkbox
                                    size={size}
                                    checked={filteredOptions?.every((option) =>
                                      value?.includes(option.value)
                                    )}
                                    indeterminate={
                                      filteredOptions?.some((option) =>
                                        value?.includes(option.value)
                                      ) &&
                                      !filteredOptions?.every((option) =>
                                        value?.includes(option.value)
                                      )
                                    }
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectAllClick();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  {needsTooltip(
                                    getSelectAllText(),
                                    currentSize.maxLabelLength
                                  ) ? (
                                    <Tooltip
                                      label={getSelectAllText()}
                                      position='top'
                                      withArrow
                                      multiline
                                      maw={250}
                                    >
                                      <Text
                                        size={currentSize.textSize}
                                        fw={500}
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {getSelectAllText()}
                                      </Text>
                                    </Tooltip>
                                  ) : (
                                    <Text
                                      size={currentSize.textSize}
                                      fw={500}
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {getSelectAllText()}
                                    </Text>
                                  )}
                                </UnstyledButton>
                              )}

                            {filteredOptions?.map((option, index) => (
                              <UnstyledButton
                                key={option.value}
                                onClick={(e) =>
                                  handleOptionToggle(option.value, e)
                                }
                                style={{
                                  width: "100%",
                                  height: `${ITEM_HEIGHT}px`,
                                  padding: `${currentSize.padding / 2}px ${
                                    currentSize.padding
                                  }px`,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: isSingleSelectionMode()
                                    ? "0px"
                                    : `${currentSize.padding}px`,
                                  cursor: "pointer",
                                  overflow: "hidden",
                                  borderRadius: "4px",
                                  margin: "2px 4px",
                                  marginBottom:
                                    index === filteredOptions?.length - 1
                                      ? "4px"
                                      : "2px",
                                  transition: "all 0.2s ease",
                                  backgroundColor:
                                    isSingleSelectionMode() &&
                                    value?.includes(option.value)
                                      ? "#6A95F9"
                                      : "transparent",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    !isSingleSelectionMode() ||
                                    !value?.includes(option.value)
                                  ) {
                                    e.currentTarget.style.backgroundColor =
                                      "#e7f5ff";
                                    e.currentTarget.style.borderColor =
                                      "#339af0";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (
                                    isSingleSelectionMode() &&
                                    value?.includes(option.value)
                                  ) {
                                    e.currentTarget.style.backgroundColor =
                                      "#6A95F9";
                                  } else {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                  }
                                  e.currentTarget.style.borderColor =
                                    "transparent";
                                }}
                              >
                                {!isSingleSelectionMode() && (
                                  <Checkbox
                                    size={size}
                                    checked={value?.includes(option.value)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleOptionToggle(option.value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                                {needsTooltip(
                                  option.label,
                                  currentSize.maxLabelLength
                                ) ? (
                                  <Tooltip
                                    label={option.label}
                                    position='top'
                                    withArrow
                                    multiline
                                    maw={250}
                                  >
                                    <Text
                                      size={currentSize.textSize}
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                        flex: 1,
                                        color:
                                          isSingleSelectionMode() &&
                                          value?.includes(option.value)
                                            ? "white"
                                            : "inherit",
                                      }}
                                    >
                                      {option.label}
                                    </Text>
                                  </Tooltip>
                                ) : (
                                  <Text
                                    size={currentSize.textSize}
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      flex: 1,
                                      color:
                                        isSingleSelectionMode() &&
                                        value?.includes(option.value)
                                          ? "white"
                                          : "inherit",
                                    }}
                                  >
                                    {option.label}
                                  </Text>
                                )}
                              </UnstyledButton>
                            ))}

                            {searchable &&
                              searchValue &&
                              filteredOptions?.length === 0 &&
                              !searchablethroughapi && (
                                <Box p={currentSize.padding} mx={4}>
                                  <Text
                                    size={currentSize.textSize}
                                    color='dimmed'
                                    ta='center'
                                  >
                                    No options found
                                  </Text>
                                </Box>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};
